export const LARGE_SCREEN_SIZE = 768;
export const AXIOS_TIMEOUT = 30000;
export const UPLOAD_AXIOS_TIMEOUT = 120000;

export const ADMIN_ROLES = ["super-admin", "owner", "admin"];
export const USER_ROLES = ["user"];

export const LOCAL_STORAGE_KEY = "local";
export const IMPERSONATION_SESSION_KEY = "sb_impersonation_active";
export const SERVER_ERR_MSG = "Server Error!";

export const STORAGE_URL = "https://salesbuckets-storage.nyc3.digitaloceanspaces.com";
export const ORGANIZATION_LOGO_DIR = "documents/organization_logos";
export const USER_PROFILE_PIC_DIR = "documents/profile_pic";
export const INVOICES_DIR = "documents/invoices";

export const SUBSCRIPTION_PERIOD_MONTHLY = "month";
export const SUBSCRIPTION_PERIOD_YEARLY = "year";

export const DEFAULT_ERROR_MSG = "Error! Something went wrong.";

export const ONBOARDING_YOUTUBE_URL = "https://www.youtube.com/@SalesBuckets";

// Re-export toast service for convenience
export { toastService } from "@/utils/toast-service";

export const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: "short",
  year: "numeric",
  month: "short",
  day: "numeric",
};

export const DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: "short",
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
};

export const NUMBER_COUNTRY_OPTIONS = {
  usa: "US",
  canada: "CA",
  uk: "GB",
  france: "FR",
  germany: "DE",
  italy: "IT",
  spain: "ES",
  singapore: "SG",
  switzerland: "CH",
  uae: "AE",
  india: "IN",
  israel: "IL",
  poland: "PL",
  mexico: "MX",
  brazil: "BR",
};

/**
 * ISO 3166-1 alpha-2 to alpha-3 country code mapping
 * Maps 2-letter country codes (from libphonenumber-js) to 3-letter ISO codes
 */
export const COUNTRY_CODE_ALPHA2_TO_ALPHA3: Record<string, string> = {
  US: "USA",
  CA: "CAN",
  GB: "GBR",
  AU: "AUS",
  NZ: "NZL",
  MX: "MEX",
  BR: "BRA",
  FR: "FRA",
  DE: "DEU",
  IT: "ITA",
  ES: "ESP",
  IN: "IND",
  CN: "CHN",
  JP: "JPN",
  KR: "KOR",
  NL: "NLD",
  BE: "BEL",
  CH: "CHE",
  AT: "AUT",
  SE: "SWE",
  NO: "NOR",
  DK: "DNK",
  FI: "FIN",
  PL: "POL",
  IE: "IRL",
  PT: "PRT",
  GR: "GRC",
  TR: "TUR",
  SA: "SAU",
  AE: "ARE",
  IL: "ISR",
  ZA: "ZAF",
  EG: "EGY",
  AR: "ARG",
  CL: "CHL",
  CO: "COL",
  PE: "PER",
  PH: "PHL",
  SG: "SGP",
  MY: "MYS",
  TH: "THA",
  ID: "IDN",
  VN: "VNM",
};

/**
 * Maps 2-letter country codes to full country names
 */
export const COUNTRY_CODE_TO_NAME: Record<string, string> = {
  US: "United States",
  CA: "Canada",
  GB: "United Kingdom",
  AU: "Australia",
  NZ: "New Zealand",
  MX: "Mexico",
  BR: "Brazil",
  FR: "France",
  DE: "Germany",
  IT: "Italy",
  ES: "Spain",
  IN: "India",
  CN: "China",
  JP: "Japan",
  KR: "South Korea",
  NL: "Netherlands",
  BE: "Belgium",
  CH: "Switzerland",
  AT: "Austria",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  PL: "Poland",
  IE: "Ireland",
  PT: "Portugal",
  GR: "Greece",
  TR: "Turkey",
  SA: "Saudi Arabia",
  AE: "United Arab Emirates",
  IL: "Israel",
  ZA: "South Africa",
  EG: "Egypt",
  AR: "Argentina",
  CL: "Chile",
  CO: "Colombia",
  PE: "Peru",
  PH: "Philippines",
  SG: "Singapore",
  MY: "Malaysia",
  TH: "Thailand",
  ID: "Indonesia",
  VN: "Vietnam",
};

/**
 * Maps US state codes to full state names
 */
export const US_STATE_CODE_TO_NAME: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
};

/**
 * Maps Canadian province codes to full province names
 */
export const CA_PROVINCE_CODE_TO_NAME: Record<string, string> = {
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

/**
 * Gets full country name from country code
 */
export const getCountryName = (countryCode: string | null | undefined): string => {
  if (!countryCode) return "";
  return COUNTRY_CODE_TO_NAME[countryCode.toUpperCase()] || countryCode;
};

/**
 * Gets full region name from region code and country code
 */
export const getRegionName = (
  regionCode: string | null | undefined,
  countryCode: string | null | undefined
): string => {
  if (!regionCode) return "";
  const upperRegion = regionCode.toUpperCase();
  const upperCountry = countryCode?.toUpperCase();

  if (upperCountry === "US") {
    return US_STATE_CODE_TO_NAME[upperRegion] || regionCode;
  }
  if (upperCountry === "CA") {
    return CA_PROVINCE_CODE_TO_NAME[upperRegion] || regionCode;
  }

  return regionCode;
};

/**
 * Formats location string from city, region, and country
 */
export const formatLocation = (
  city: string | null | undefined,
  region: string | null | undefined,
  countryCode: string | null | undefined
): string => {
  const parts: string[] = [];

  if (city) {
    parts.push(city);
  }

  if (region) {
    const regionName = getRegionName(region, countryCode);
    parts.push(regionName);
  }

  if (countryCode) {
    const countryName = getCountryName(countryCode);
    parts.push(countryName);
  }

  return parts.join(", ") || "";
};

export const FNAME_KEYS = [
  "Firstname",
  "First Name",
  "first_name",
  "firstName",
  "FIRST NAME",
  "FIRST NAME1",
  "FIRST NAME2",
  "FIRST NAME_contact",
  "FIRST NAME_personal",
  "FIRST NAME_work",
  "FIRST-NAME",
  "FIRST-NAME1",
  "FIRST-NAME_work",
  "FIRST.NAME1",
  "FIRST.NAME2",
  "FIRST.NAME_work",
  "FIRSTNAME",
  "FIRSTNAME2",
  "FIRSTNAME_contact",
  "FIRSTNAME_work",
  "FIRST_NAME2",
  "FIRST_NAME_contact",
  "FIRST_NAME_work",
  "First-name_contact",
  "First-name_personal",
  "First.name",
  "First.name_contact",
  "First.name_personal",
  "FirstName1",
  "FirstName_personal",
  "First_name",
  "First_name1",
  "First_name_personal",
  "Given Name",
  "GivenName",
  "contact_FIRST NAME",
  "contact_FIRST.NAME",
  "contact_FIRST_NAME",
  "contact_First-name",
  "contact_FirstName",
  "f_name",
  "first",
  "first-name2",
  "given_name",
  "personal_FIRST NAME",
  "personal_FIRST-NAME",
  "personal_FIRST.NAME",
  "personal_FIRSTNAME",
  "personal_first_name",
  "work_FIRSTNAME",
  "work_FIRST_NAME",
  "work_First Name",
  "work_First-name",
  "work_First.name",
  "First Name (Linkedin)",
  "First Name",
  "First name",
  "first_name",
  "First Name",
  "FirstName",
];

export const LNAME_KEYS = [
  "lastName",
  "Family Name",
  "FamilyName",
  "LAST NAME1",
  "LAST NAME2",
  "LAST-NAME",
  "LAST-NAME1",
  "LAST-NAME_contact",
  "LAST-NAME_work",
  "LAST.NAME",
  "LAST.NAME1",
  "LAST.NAME2",
  "LAST.NAME_contact",
  "LAST.NAME_personal",
  "LAST.NAME_work",
  "LASTNAME2",
  "LASTNAME_personal",
  "LASTNAME_work",
  "LAST_NAME2",
  "LAST_NAME_contact",
  "LAST_NAME_personal",
  "LAST_NAME_work",
  "LNAME",
  "Last Name",
  "Last Name_contact",
  "Last Name_personal",
  "Last Name_work",
  "Last-name2",
  "Last-name_personal",
  "Last.name",
  "Last.name1",
  "Last.name2",
  "Last.name_personal",
  "Last.name_work",
  "LastName",
  "LastName1",
  "LastName_personal",
  "LastName_work",
  "Last_name_work",
  "Lastname",
  "Lastname2",
  "Lastname_contact",
  "Lastname_personal",
  "Lastname_work",
  "Surname",
  "contact_LAST NAME",
  "contact_LASTNAME",
  "contact_Last Name",
  "contact_Last.name",
  "contact_Last_name",
  "contact_Lastname",
  "contact_last name",
  "contact_last.name",
  "contact_last_name",
  "contact_lastname",
  "family_name",
  "l_name",
  "last",
  "last name1",
  "last name_work",
  "last-name_contact",
  "last-name_personal",
  "last.name",
  "last.name1",
  "last.name_contact",
  "last.name_personal",
  "last.name_work",
  "last_name",
  "last_name1",
  "last_name2",
  "last_name_personal",
  "lastname",
  "lastname1",
  "lastname2",
  "lastname_contact",
  "lastname_personal",
  "lastname_work",
  "lname",
  "personal_LAST.NAME",
  "personal_LAST_NAME",
  "personal_Last Name",
  "personal_Last-name",
  "personal_Last.name",
  "personal_LastName",
  "personal_Last_name",
  "personal_last-name",
  "personal_last.name",
  "personal_last_name",
  "personal_lastname",
  "work_LAST NAME",
  "work_LAST-NAME",
  "work_LAST.NAME",
  "work_LASTNAME",
  "work_LAST_NAME",
  "work_Last Name",
  "work_Last.name",
  "work_last.name",
  "work_lastname",
  "Last Name (Linkedin)",
  "Last Name",
  "Last name",
  "last_name",
  "Last Name",
  "LastName",
];

export const EMAIL_KEYS = [
  "Email",
  "Professional Email",
  "Business Email",
  "email",
  "E-mail",
  "EMAIL",
  "EMAIL1",
  "EMAIL2",
  "EMAILID",
  "EMAIL_ADDRESS",
  "EMAIL_contact",
  "EMAIL_personal",
  "EMAIL_work",
  "Email Address",
  "Email.",
  "Emaill",
  "Emial",
  "contact_EMAIL",
  "personal_EMAIL",
  "work_EMAIL",
  "Email (FullEnrich)",
  "Email",
  "Work email",
  "email",
  "Email Address",
  "Work Email",
  "Email 1",
];

export const MOBILE_NUMBER_KEYS = [
  "Personal Phone Number",
  "Contact Phone",
  "Mobile phone",
  "mobilePhone",
  "CELL",
  "Cell Number",
  "Cellular",
  "MOBILE NUMBER",
  "MOBILE NUMBER_personal",
  "MOBILE-NUMBER_contact",
  "MOBILE-NUMBER_personal",
  "MOBILE.NUMBER1",
  "MOBILE.NUMBER_contact",
  "MOBILE.NUMBER_personal",
  "MOBILE.NUMBER_work",
  "MOBILENUMBER",
  "MOBILENUMBER_work",
  "MOBILE_NUMBER",
  "MOBILE_NUMBER2",
  "MOBILE_NUMBER_contact",
  "MOBILE_NUMBER_personal",
  "Mobile #",
  "Mobile Number1",
  "Mobile Number_contact",
  "Mobile Number_work",
  "Mobile Phone",
  "Mobile-number",
  "Mobile-number2",
  "Mobile-number_work",
  "Mobile.number",
  "Mobile.number2",
  "MobileNumber1",
  "MobileNumber2",
  "MobileNumber_personal",
  "Mobile_number_work",
  "contact_MOBILE NUMBER",
  "contact_MOBILE-NUMBER",
  "contact_MOBILE.NUMBER",
  "contact_MOBILENUMBER",
  "contact_MOBILE_NUMBER",
  "mobile-number1",
  "mobile_phone",
  "mobilenumber_contact",
  "personal_MOBILE NUMBER",
  "personal_MOBILE.NUMBER",
  "personal_MOBILE_NUMBER",
  "personal_Mobilenumber",
  "personal_mobile-number",
  "work_MOBILE NUMBER",
  "work_MOBILE-NUMBER",
  "work_MOBILE.NUMBER",
  "work_MOBILE_NUMBER",
  "work_mobilenumber",
  "All Mobile Phone Numbers (FullEnrich)",
  "Mobile Phone",
  "mobile_phone1",
  "Phone 1",
  "Mobile Phone",
  "Contact Phone 1",
  "Contact Mobile Phone",
  "Contact Mobile Phone 2",
  "Contact Mobile Phone 3",
  "MobileDirectDial",
];

export const DIRECT_NUMBER_KEYS = [
  "Direct Phone",
  "Direct Phone Number",
  "Professional Phone",
  "direct_phone",
  "directPhone",
  "DIRECT NUMBER",
  "DIRECT NUMBER1",
  "DIRECT NUMBER2",
  "DIRECT NUMBER_contact",
  "DIRECT NUMBER_personal",
  "DIRECT-NUMBER",
  "DIRECT-NUMBER1",
  "DIRECT-NUMBER2",
  "DIRECT-NUMBER_contact",
  "DIRECT-NUMBER_personal",
  "DIRECT.NUMBER",
  "DIRECT.NUMBER_contact",
  "DIRECT.NUMBER_personal",
  "DIRECTDIAL",
  "DIRECTNUMBER",
  "DIRECTNUMBER2",
  "DIRECTNUMBER_contact",
  "DIRECTNUMBER_personal",
  "DIRECTNUMBER_work",
  "DIRECT_NUMBER",
  "DIRECT_NUMBER1",
  "DIRECT_NUMBER2",
  "DIRECT_NUMBER_contact",
  "DIRECT_NUMBER_personal",
  "DIRECT_NUMBER_work",
  "Direct Dial Number",
  "Direct Line",
  "Direct Number",
  "Direct Number_work",
  "Direct-number1",
  "Direct-number_contact",
  "Direct-number_work",
  "Direct.number1",
  "Direct.number2",
  "Direct.number_work",
  "DirectDial",
  "DirectNumber",
  "DirectNumber2",
  "DirectNumber_contact",
  "DirectNumber_personal",
  "Direct_number",
  "Direct_number1",
  "Direct_number_personal",
  "Directnumber",
  "Directnumber1",
  "Directnumber_contact",
  "Office Phone",
  "Work Phone",
  "contact_DIRECT-NUMBER",
  "contact_DIRECTNUMBER",
  "contact_DIRECT_NUMBER",
  "contact_Direct Number",
  "contact_Direct.number",
  "contact_DirectNumber",
  "contact_Directnumber",
  "contact_direct number",
  "contact_direct.number",
  "contact_directnumber",
  "direct number1",
  "direct number2",
  "direct number_personal",
  "direct-number",
  "direct-number1",
  "direct-number2",
  "direct-number_work",
  "direct.number",
  "direct.number1",
  "direct.number_personal",
  "direct_number1",
  "direct_number2",
  "direct_number_work",
  "directnumber",
  "directnumber1",
  "directnumber2",
  "directnumber_contact",
  "personal_DIRECT NUMBER",
  "personal_DIRECT-NUMBER",
  "personal_DIRECT.NUMBER",
  "personal_DIRECTNUMBER",
  "personal_DIRECT_NUMBER",
  "personal_Direct Number",
  "personal_Direct-number",
  "personal_Direct.number",
  "personal_DirectNumber",
  "personal_Direct_number",
  "personal_direct number",
  "personal_direct-number",
  "personal_direct.number",
  "personal_direct_number",
  "personal_directnumber",
  "work_DIRECT-NUMBER",
  "work_DIRECTNUMBER",
  "work_DIRECT_NUMBER",
  "work_Direct Number",
  "work_Direct-number",
  "work_Direct.number",
  "work_Direct_number",
  "work_Directnumber",
  "work_direct number",
  "work_direct.number",
  "All Landline or Work Phone Numbers (FullEnrich)",
  "Other Phone",
  "phone_number1",
  "Phone 2",
  "Direct Phone Number",
  "Work Phone",
  "Contact Phone 2",
  "PhoneNumber",
];

export const COMPANY_KEYS = [
  "Company",
  "company_name",
  "companyName",
  "Business Name",
  "COMPANY",
  "COMPANY1",
  "COMPANY2",
  "COMPANYNAME",
  "COMPANY_contact",
  "COMPANY_personal",
  "COMPANY_work",
  "CONAME",
  "Company Name",
  "Employer",
  "Org Name",
  "Organisation",
  "Organization",
  "co_name",
  "contact_COMPANY",
  "personal_COMPANY",
  "work_COMPANY",
  "Company Name (Linkedin)",
  "Company",
  "Company name",
  "company",
  "Company Name",
  "Company",
  "Company Name - Cleaned",
  "Company Name - Raw",
  "CompanyName",
];

export const COMPANY_HQ_KEYS = [
  "Company Location - City",
  "Company Street Address",
  "companyHQ",
  "COMPANY HQ1",
  "COMPANY HQ_contact",
  "COMPANY HQ_work",
  "COMPANY-HQ",
  "COMPANY-HQ1",
  "COMPANY-HQ2",
  "COMPANY-HQ_work",
  "COMPANY.HQ_contact",
  "COMPANY.HQ_work",
  "COMPANYHQ",
  "COMPANYHQ1",
  "COMPANYHQ_contact",
  "COMPANYHQ_personal",
  "COMPANYHQ_work",
  "COMPANYNAME",
  "COMPANY_HQ",
  "COMPANY_HQ2",
  "COMPANY_HQ_contact",
  "COMPANY_HQ_personal",
  "CoName",
  "Company HQ",
  "Company Name",
  "Company.hq",
  "Company.hq1",
  "Company.hq2",
  "Company.hq_contact",
  "CompanyHQ2",
  "CompanyHQ_contact",
  "CompanyHQ_work",
  "Company_hq",
  "Company_hq2",
  "Company_hq_personal",
  "Company_hq_work",
  "Companyhq",
  "Companyhq1",
  "Companyhq2",
  "Companyhq_contact",
  "Employer",
  "Org Name",
  "Organisation",
  "Organization",
  "co_name",
  "company hq",
  "company hq1",
  "company hq2",
  "company-hq",
  "company-hq1",
  "company-hq2",
  "company-hq_contact",
  "company-hq_work",
  "company.hq_contact",
  "company.hq_work",
  "company_hq",
  "company_hq1",
  "company_hq_personal",
  "company_hq_work",
  "companyhq1",
  "companyhq_work",
  "contact_COMPANY HQ",
  "contact_COMPANY-HQ",
  "contact_COMPANYHQ",
  "contact_COMPANY_HQ",
  "contact_Company Hq",
  "contact_CompanyHQ",
  "contact_Company_hq",
  "contact_Companyhq",
  "contact_company-hq",
  "contact_company.hq",
  "contact_company_hq",
  "personal_COMPANY HQ",
  "personal_COMPANY-HQ",
  "personal_COMPANY.HQ",
  "personal_COMPANYHQ",
  "personal_COMPANY_HQ",
  "personal_Company_hq",
  "personal_Companyhq",
  "personal_company hq",
  "personal_company-hq",
  "personal_company.hq",
  "personal_company_hq",
  "personal_companyhq",
  "work_COMPANY HQ",
  "work_COMPANY-HQ",
  "work_COMPANY.HQ",
  "work_Company-hq",
  "work_Company.hq",
  "work_CompanyHQ",
  "work_Companyhq",
  "work_company hq",
  "work_company-hq",
  "work_company.hq",
  "work_companyhq",
  "Company Headquarters Address Line 1 (Linkedin)",
  "Company Address",
  "Company city",
  "company_location",
  "Full Address",
  "Company Location",
  "Company Location",
  "CompanyAddress",
];

export const WEBSITE_KEYS = [
  "Website",
  "Domain",
  "Company Domain Name",
  "website",
  "Company Website (Linkedin)",
  "Website",
  "Domain (FullEnrich)",
  "Company website",
  "company_domain",
  "Website",
  "Domain",
  "Company Website Domain",
];

export const JOB_TITLE_KEYS = [
  "Title",
  "job_title",
  "jobTitle",
  "Designation",
  "Job Role",
  "Job Title",
  "Position",
  "Professional Title",
  "ROLE",
  "TITLE",
  "TITLE1",
  "TITLE2",
  "TITLE_contact",
  "TITLE_personal",
  "TITLE_work",
  "contact_TITLE",
  "personal_TITLE",
  "work_TITLE",
  "Job Title (Linkedin)",
  "Title",
  "Job title",
  "title",
  "Job Title",
];

export const CONTACT_LINKEDIN_KEYS = [
  "LinkedIn URL (Contact)",
  "LinkedIn Contact Profile URL",
  "LinkedIn URL",
  "LinkedIn Profile",
  "LinkedIn Profile URL",
  "linkedin_url",
  "linkedinUrl",
  "LI URL",
  "LINKEDIN",
  "LINKEDIN URL",
  "LINKEDIN URL_personal",
  "LINKEDIN-URL",
  "LINKEDIN-URL1",
  "LINKEDIN-URL2",
  "LINKEDIN-URL_contact",
  "LINKEDIN-URL_personal",
  "LINKEDIN.URL",
  "LINKEDIN.URL_personal",
  "LINKEDIN.URL_work",
  "LINKEDINURL2",
  "LINKEDINURL_personal",
  "LINKEDINURL_work",
  "LINKEDIN_URL1",
  "LINKEDIN_URL2",
  "LINKEDIN_URL_personal",
  "LINKEDIN_URL_work",
  "LinkedIn URL1",
  "LinkedIn URL_contact",
  "LinkedIn URL_work",
  "LinkedInURL",
  "LinkedInURL1",
  "Linkedin Url2",
  "Linkedin-url_work",
  "Linkedin.url1",
  "Linkedin.url2",
  "Linkedin.url_contact",
  "Linkedin_url",
  "Linkedinurl_contact",
  "contact_LINKEDIN.URL",
  "contact_LINKEDINURL",
  "contact_LinkedIn URL",
  "contact_Linkedin_url",
  "linkedin.com/in/",
  "linkedin_profile",
  "linkedin_url_contact",
  "personal_LINKEDIN-URL",
  "personal_LINKEDIN_URL",
  "personal_LinkedIn URL",
  "personal_LinkedInURL",
  "work_LINKEDIN URL",
  "work_LINKEDIN.URL",
  "work_LINKEDINURL",
  "work_LINKEDIN_URL",
  "work_Linkedin-url",
  "LinkedIn Profile Url",
  "Linkedin Url",
  "Linkedin Url(FullEnrich)",
  "Person Linkedin Url",
  "Linkedin URL",
  "linkedin",
  "LinkedIn Contact Profile URL",
  "Social",
  "Contact LI Profile URL",
  "ContactLinkedIn",
];

export const COMPANY_LINKEDIN_KEYS = [
  "LinkedIn URL (Company)",
  "LinkedIn Company Profile URL",
  "Company LinkedIn Profile",
  "company_linkedin_url",
  "companyLinkedinUrl",
  "COMPANY LINKEDIN URL",
  "COMPANY LINKEDIN URL1",
  "COMPANY LINKEDIN URL_personal",
  "COMPANY-LINKEDIN-URL",
  "COMPANY-LINKEDIN-URL2",
  "COMPANY-LINKEDIN-URL_personal",
  "COMPANY.LINKEDIN.URL1",
  "COMPANY.LINKEDIN.URL2",
  "COMPANY.LINKEDIN.URL_personal",
  "COMPANY.LINKEDIN.URL_work",
  "COMPANYLINKEDINURL1",
  "COMPANYLINKEDINURL2",
  "COMPANYLINKEDINURL_contact",
  "COMPANYLINKEDINURL_work",
  "COMPANYNAME",
  "COMPANY_LINKEDIN_URL_contact",
  "COMPANY_LINKEDIN_URL_personal",
  "COMPANY_LINKEDIN_URL_work",
  "CoName",
  "Company LinkedIn URL",
  "Company LinkedIn Url",
  "Company Linkedin URL",
  "company_linkedin",
  "LinkedIn Company Profile URL",
  "Company LI Profile Url",
  "CompanyLinkedIn",
];

export const COMPANY_PHONENUMBER_KEYS = [
  "Company Phone Number",
  "Company HQ Phone",
  "Corporate Phone",
  "Company Phone",
  "Company HQ Phone",
  "Company Phone",
  "Company Phone 1",
];

export const COMPANY_SIZE_KEYS = [
  "Company Headcount/Headcount",
  "Company Headcount",
  "Employees Count",
  "Headcount",
  "Company Headcount",
  "Employee Count",
  "company_size",
  "companySize",
  "Company Headcount (Linkedin)",
  "Company Headcount Range (Linkedin)",
  "# Employees",
  "Company number of employees",
  "company_size",
  "Company Headcount",
  "Company Staff Count Range",
  "NumberEmployees",
];

export const MOBILE_PHONE_STATUS_KEYS = [
  "Phone Status",
  "Phone_Status",
  "phone_status",
  "PhoneStatus",
  "phoneStatus",
  "phone status",
  "Mobile Phone Status",
  "mobile_phone_status",
  "MobilePhoneStatus",
];

export const DIRECT_PHONE_STATUS_KEYS = [
  "Direct Phone Status",
  "Direct_Phone_Status",
  "direct_phone_status",
  "DirectPhoneStatus",
];

export const INDUSTRY_KEYS = [
  "Business Sector",
  "Primary Industry",
  "Field of Business",
  "Industry",
  "INDUSTRY",
  "INDUSTRY1",
  "INDUSTRY2",
  "INDUSTRY_contact",
  "INDUSTRY_personal",
  "INDUSTRY_work",
  "Industry Sector",
  "Sector",
  "Vertical",
  "contact_INDUSTRY",
  "line_of_business",
  "personal_INDUSTRY",
  "work_INDUSTRY",
  "Company Industry (Linkedin)",
  "Industry",
  "Company main industry",
  "company_industry",
  "Primary Industry",
  "Industry",
  "Company Industry",
  "Industry Tags",
  "IndustryTags",
];

export const OPPORTUNITY_CONFIDENCE_VALUES = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

export const INDUSTRY_OPTIONS = [
  { label: "Technology", value: "Technology" },
  { label: "Healthcare", value: "Healthcare" },
  { label: "Finance", value: "Finance" },
  { label: "Education", value: "Education" },
  { label: "Retail", value: "Retail" },
  { label: "Manufacturing", value: "Manufacturing" },
  { label: "Real Estate", value: "Real Estate" },
  { label: "Consulting", value: "Consulting" },
  { label: "Other", value: "Other" },
];
